import React from 'react';
import JobCategoryPage from './JobCategoryPage';
import { Building2 } from 'lucide-react';

const HospitalSocialWorkerJobs = () => {
  const contentSections = [
    {
      title: "What is a Hospital Social Worker?",
      content: (
        <p>
          Hospital social workers are licensed professionals who provide critical support services to patients and their families during medical crises, hospitalizations, and transitions of care. Working within acute care settings, these professionals bridge the gap between medical treatment and the social, emotional, and practical needs of patients. Hospital social workers are essential members of interdisciplinary healthcare teams, collaborating with physicians, nurses, case managers, and other specialists to ensure comprehensive patient care.
        </p>
      )
    },
    {
      title: "Hospital Social Worker Job Responsibilities",
      content: (
        <p>
          Hospital social workers handle a diverse range of responsibilities that directly impact patient outcomes and family well-being. Their work encompasses discharge planning, crisis intervention, resource coordination, and emotional support during some of life's most challenging moments.
        </p>
      ),
      list: [
        "Conduct psychosocial assessments for admitted patients",
        "Develop and coordinate discharge plans with healthcare teams",
        "Provide crisis intervention and emotional support to patients and families",
        "Connect patients with community resources and post-acute care services",
        "Assist with advance directive discussions and end-of-life planning",
        "Advocate for patient rights and access to care",
        "Facilitate family meetings and care conferences",
        "Document clinical interventions in electronic health records",
        "Coordinate with insurance companies for authorizations and appeals"
      ]
    },
    {
      title: "Required Qualifications and Licenses",
      content: (
        <p>
          Hospital social work positions typically require specific educational backgrounds and professional licensure. Most acute care hospitals prefer or require candidates with a Master of Social Work (MSW) degree and clinical licensure such as LCSW (Licensed Clinical Social Worker) or LMSW (Licensed Master Social Worker). Some entry-level positions may accept LBSW (Licensed Bachelor Social Worker) credentials with supervision requirements.
        </p>
      ),
      list: [
        "Master of Social Work (MSW) from a CSWE-accredited program preferred",
        "State licensure (LCSW, LMSW, or LBSW depending on position level)",
        "Knowledge of healthcare systems and medical terminology",
        "Experience with discharge planning and case management",
        "Strong communication and documentation skills",
        "Ability to work in fast-paced, high-pressure environments"
      ]
    },
    {
      title: "Salary Expectations for Hospital Social Workers",
      content: (
        <p>
          Hospital social worker salaries vary based on location, experience level, licensure status, and facility type. According to recent data, entry-level hospital social workers with LMSW credentials can expect starting salaries ranging from $45,000 to $55,000 annually. Experienced LCSWs in hospital settings often earn between $60,000 and $85,000 or more, with supervisory roles and specialized departments commanding higher compensation. Major metropolitan areas and academic medical centers typically offer the highest salary ranges. Many hospital positions also include comprehensive benefits packages, including health insurance, retirement plans, continuing education allowances, and paid time off.
        </p>
      )
    },
    {
      title: "Career Growth and Advancement",
      content: (
        <p>
          Hospital social work offers numerous pathways for career advancement and specialization. Social workers can progress from staff positions to senior roles, supervisory positions, department directors, and administrative leadership. Specialization opportunities include oncology social work, transplant services, emergency department social work, pediatric care, and palliative/hospice care. Many hospital social workers also pursue additional certifications such as the Academy of Certified Social Workers (ACSW) or specialized healthcare certifications to enhance their expertise and marketability.
        </p>
      )
    },
    {
      title: "Why Choose Hospital Social Work?",
      content: (
        <p>
          Hospital social work offers a unique opportunity to make immediate, tangible differences in people's lives during their most vulnerable moments. The fast-paced environment provides variety and intellectual challenge, while the interdisciplinary setting allows for professional collaboration and growth. Hospital social workers develop expertise in medical social work, crisis intervention, and healthcare systems navigation that is highly valued across the profession. For those who thrive in dynamic environments and want to combine clinical skills with advocacy, hospital social work represents a rewarding and impactful career path.
        </p>
      )
    }
  ];

  const relatedCategories = [
    { name: "Medical Social Worker Jobs", href: "/medical-social-worker-jobs" },
    { name: "Hospice Social Worker Jobs", href: "/hospice-social-worker-jobs" },
    { name: "Clinical Social Worker Jobs", href: "/clinical-social-worker-jobs" },
    { name: "School Social Worker Jobs", href: "/school-social-worker-jobs" },
  ];

  return (
    <JobCategoryPage
      category="hospital-social-worker-jobs"
      title="Hospital Social Worker Jobs"
      metaTitle="Hospital Social Worker Jobs | LCSW & LMSW Hospital Positions | INeedASocialWorker"
      metaDescription="Find hospital social worker jobs nationwide. Browse LCSW and LMSW positions at hospitals and medical centers. Discharge planning, crisis intervention, and patient advocacy roles. Apply with SW Express Apply™."
      heroTitle="Hospital Social Worker Jobs"
      heroSubtitle="Find rewarding hospital social work positions at leading medical centers, academic hospitals, and healthcare systems across the United States."
      searchKeywords={["hospital social worker", "hospital social work", "acute care social worker", "medical center social worker", "healthcare social worker", "inpatient social worker"]}
      contentSections={contentSections}
      relatedCategories={relatedCategories}
      iconComponent={Building2}
    />
  );
};

export default HospitalSocialWorkerJobs;
